import { useCallback, useEffect, useState } from 'react';

/**
 * "Show while it works" — ONE view preference for every AI chat surface (btgpt,
 * the agent builder on the web and in the extension; rev 3.3 §3). Every level
 * opens the stream; the level only filters what the live turn renders:
 *
 *   quiet     the status line only; the answer appears whole when it is done
 *   activity  + the steps (tool chips, narration, a failed step) and the answer as it is written
 *   thinking  + the model's summarized thinking (live only, never saved)
 *   trace     + a timestamped log: phases, pods, tool arguments, results' size / status / duration
 *
 * Replaces the old on/off `bt.ai.liveStream` key: ON → activity, OFF → quiet
 * (migrated once, then the old key is removed). In the extension this is
 * web.whatsapp.com's storage — fine for a per-device viewing preference. Every
 * access is guarded: storage can be missing or throw.
 */
export type AiDetailLevel = 'quiet' | 'activity' | 'thinking' | 'trace';

export const DETAIL_LEVELS: readonly AiDetailLevel[] = ['quiet', 'activity', 'thinking', 'trace'];

export const DETAIL_LEVEL_STORAGE_KEY = 'bt.ai.detail';
/**
 * MIGRATION (rev 3.3, 2026-09-27): the retired on/off key ('0' = off), read once and
 * removed. Delete this constant and the marked block in `readDetailLevel` once the
 * old key is gone from the field (every browser that had it has loaded a chat once).
 */
export const LEGACY_LIVE_STREAM_KEY = 'bt.ai.liveStream';

export const DEFAULT_DETAIL_LEVEL: AiDetailLevel = 'activity';

const CHANGE_EVENT = 'bt-ai-detail-change';

const isLevel = (v: unknown): v is AiDetailLevel => typeof v === 'string' && (DETAIL_LEVELS as readonly string[]).includes(v);

/** The last choice made on this page — what counts when storage is unavailable. */
let pageValue: AiDetailLevel = DEFAULT_DETAIL_LEVEL;

export function readDetailLevel(): AiDetailLevel {
  try {
    const storage = window.localStorage;
    const stored = storage.getItem(DETAIL_LEVEL_STORAGE_KEY);
    // MIGRATION (rev 3.3): one-time move from the on/off switch — see LEGACY_LIVE_STREAM_KEY.
    const legacy = storage.getItem(LEGACY_LIVE_STREAM_KEY);
    if (isLevel(stored)) {
      if (legacy !== null) storage.removeItem(LEGACY_LIVE_STREAM_KEY); // stale: the new key already wins
      return stored;
    }
    if (legacy === null) return pageValue;
    const migrated: AiDetailLevel = legacy === '0' ? 'quiet' : 'activity';
    storage.setItem(DETAIL_LEVEL_STORAGE_KEY, migrated);
    storage.removeItem(LEGACY_LIVE_STREAM_KEY);
    pageValue = migrated;
    return migrated;
  } catch {
    return pageValue;
  }
}

function writeDetailLevel(level: AiDetailLevel): void {
  pageValue = level;
  try {
    window.localStorage.setItem(DETAIL_LEVEL_STORAGE_KEY, level);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: level }));
}

/** `[level, setLevel]` — every mounted chat re-renders when any of them changes it. */
export function useDetailLevel(): [AiDetailLevel, (level: AiDetailLevel) => void] {
  const [level, setLevel] = useState<AiDetailLevel>(readDetailLevel);
  useEffect(() => {
    const sync = () => setLevel(readDetailLevel());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);
  const set = useCallback((next: AiDetailLevel) => {
    setLevel(next);
    writeDetailLevel(next);
  }, []);
  return [level, set];
}
